import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { hasAnyFieldEmpty } from '@libs/helpers';
import { ErrorMessageTexts, useApiResponse } from '@libs/hooks';
import { CalculateExchangeNewArgs, calculateExchangeNewQuery } from 'src/gql';

const EXCHANGE_RATE_POLL_INTERVAL = 5000; //ms
type ExchangeRateProps = Partial<CalculateExchangeNewArgs>;
export const useExchangeRateGe = (
  record: ExchangeRateProps,
  shouldSkipCalculation?: boolean
) => {
  const [hasError, setHasError] = useState(false);

  const res = useQuery(calculateExchangeNewQuery, {
    variables: {
      // NOTE: "as" is needed here because we are skipping the api call if any of the fields of record are empty
      record: record as CalculateExchangeNewArgs,
    },
    fetchPolicy: 'network-only',
    pollInterval: EXCHANGE_RATE_POLL_INTERVAL,
    skip: hasAnyFieldEmpty(record) || !record.amount || shouldSkipCalculation,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (hasError) {
      res.stopPolling();
      return;
    }
    if (!(hasAnyFieldEmpty(record) || !record.amount)) {
      res.stopPolling();
      res.startPolling(EXCHANGE_RATE_POLL_INTERVAL);
    }
    return () => res.stopPolling();
  }, [hasError, res, record]);

  useEffect(() => {
    if (record.amount) {
      setHasError(false);
    }
  }, [record.amount]);

  const { response, loading, errorMessage, clearErrorMessage } = useApiResponse(
    {
      ...res,
      errors: res.error,
    },
    undefined,
    () => setHasError(true)
  );

  const limitErrorMessage = useMemo(() => {
    if (!response?.calculateExchangeNew?.error) return '';

    const errorMsg =
      ErrorMessageTexts[response?.calculateExchangeNew.error.type];
    return errorMsg;
  }, [response?.calculateExchangeNew?.error]);

  return {
    response,
    exchangeRate: response?.calculateExchangeNew,
    loading,
    errorMessage,
    limitErrorMessage,
    clearErrorMessage,
    maxAmount: response?.calculateExchangeNew?.error?.details.max,
    minAmount: response?.calculateExchangeNew?.error?.details.minimumVolume,
    minCost: response?.calculateExchangeNew?.error?.details.minimumCost,
    isWithinLimit: !response?.calculateExchangeNew?.error,
    minCostAssetCode: response?.calculateExchangeNew?.error?.details.assetCode,
  };
};
